import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import LandingPage from 'pages/landing/LandingPage';
import './App.css';
import 'styles/tailwind/output.css'
import PricingPage from 'pages/landing/PricingPage';
import TestComponent from 'test/TestComponent';
import SignUpPage from 'pages/auth/SignUpPage';
import LoginPage from 'pages/auth/LoginPage';
import BlogPage from 'pages/blog/BlogPage';
import BlogPostPage from 'pages/blog/BlogPostPage';
import AccountTypePage from 'pages/auth/AccountTypePage';
import SuccessErrorPage from 'pages/auth/SuccessErrorPage';
import AcceptInvitePage from 'pages/auth/AcceptInvitePage';
import ReportCategoriesPage from 'pages/report/ReportCategoriesPage';
import DataPrivacyPage from 'pages/legal/DataPrivacyPage';
import LegalPage from 'pages/legal/LegalPage';
import TermsPage from 'pages/legal/TermsPage';
import ContactUsPage from 'layouts/section/ContactUsPage';
import WorkspacePage from 'pages/landing/WorkspacePage';
import AboutUsPage from 'pages/landing/AboutUsPage';
import ReportPage from 'pages/report/ReportPage';
import SignUpPaymentPage from 'pages/auth/SignUpPaymentPage';
import ProfilePage from 'pages/profile/ProfilePage';
import ReportPaymentPage from 'pages/report/ReportPaymentPage';
import SubscriptionPaymentPage from 'pages/user/SubscriptionPaymentPage';
import ChangePlanPage from 'pages/user/ChangePlanPage';
import TeamsPage from 'pages/team/TeamsPage';
import SelectedReportCategoryPage from 'pages/report/SelectedReportCategoryPage';
import CareerPage from 'pages/team/CareerPage';
import FAQPage from 'pages/faq/FAQPage';
import ChangePlanInvoicePage from 'pages/user/ChangePlanInvoicePage';
import RequirePaymentGate from 'routes/RequirePaymentGate';
import RequireChangePasswordGate from 'routes/RequireChangePasswordGate';
import VerifyEmailPage from 'pages/auth/VerifyEmailPage';
import ForgotPasswordPage from 'pages/auth/ForgotPasswordPage';
import ResetPasswordPage from 'pages/auth/ResetPasswordPage';
import ChangePasswordRequiredPage from 'pages/auth/ChangePasswordRequiredPage';
import { RouteTracker } from './tracking';
import { MaintenanceGate, SiteWideNotices } from 'maintenance/index';

/**
 * App component defines the main application structure with routing setup.
 *
 * @component
 *
 * @example
 * // Renders the main application with defined routes
 * import React from 'react';
 * import App from './App';
 *
 * const MainApp = () => <App />;
 * export default MainApp;
 */
const App: React.FC = () => {
  return (
    <Router>
      <RouteTracker />
      <div className="App">
        {/** The one site-wide strip: whole-product maintenance notices only */}
        <SiteWideNotices />
        <Routes>
          {/**
           * Every route group below is wrapped in a MaintenanceGate for its own area, so a
           * scoped maintenance window blocks (or announces itself on) only the pages of that
           * area. A whole-product window reaches every gate because the server includes it in
           * each area's answer. /success, /error and /test belong to no area and are never
           * blocked: /success and /error are shared result pages that must always render.
           */}

          {/**
           * Forced first-login password change — Pilot admin-created accounts.
           * Deliberately OUTSIDE RequireChangePasswordGate below: that gate
           * redirects here whenever mustChangePassword is true, so this route
           * must never be wrapped by its own gate or every render would
           * redirect to itself.
           */}
          <Route element={<MaintenanceGate area="accounts" />}>
            <Route path="/change-password-required" element={<ChangePasswordRequiredPage />} />
          </Route>

          {/**
           * Everything else lives behind RequireChangePasswordGate: a logged-in
           * user whose account still has a system-generated password is locked
           * to the route above regardless of which URL they land on (typed
           * directly, clicked the logo, browser back button) - it isn't enough
           * to only gate the workspace handoff, since every other page here
           * was otherwise fully reachable with a valid session.
           */}
          <Route element={<RequireChangePasswordGate />}>
            {/** ----- Accounts ----- */}
            <Route element={<MaintenanceGate area="accounts" />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/account-type" element={<AccountTypePage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/** Accept Org Invite Route — shown after invited user creates free account */}
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
            </Route>

            {/** ----- Billing ----- */}
            <Route element={<MaintenanceGate area="billing" />}>
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/sign-up-payment" element={<SignUpPaymentPage />} />
              <Route path="/subscription/payment" element={<SubscriptionPaymentPage />} />
              <Route path="/change-plan" element={<ChangePlanPage />} />
            </Route>

            {/** ----- Reports (browsing) ----- */}
            <Route element={<MaintenanceGate area="reports" />}>
              <Route path="/report/categories" element={<ReportCategoriesPage />} />
              <Route path="/report/category/:id" element={<SelectedReportCategoryPage />} />
            </Route>

            {/** ----- Report purchase ----- */}
            <Route element={<MaintenanceGate area="report-purchase" />}>
              <Route path="/report/payment/:id" element={<ReportPaymentPage />} />
            </Route>

            {/** ----- Blog ----- */}
            <Route element={<MaintenanceGate area="blog" />}>
              <Route path="/blogs" element={<BlogPage />} />
              <Route path="/blog/:id" element={<BlogPostPage />} />
            </Route>

            {/** ----- Website ----- */}
            <Route element={<MaintenanceGate area="website" />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/team" element={<TeamsPage />} />
              <Route path="/career" element={<CareerPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="/privacy" element={<DataPrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/** Workspace hand-off page */}
              <Route path="/workspace" element={<WorkspacePage />} />
            </Route>

            {/** ----- Shared result pages and test route: no area, never blocked ----- */}
            <Route path="/success" element={<SuccessErrorPage />} />
            <Route path="/error" element={<SuccessErrorPage />} />
            <Route path="/test" element={<TestComponent />} />

            {/** Redirect unknown route to LandingPage */}
            <Route path="*" element={<Navigate to="/" replace />} />

            {/** ----- Protected Routes ----- */}
            <Route element={<RequirePaymentGate />}>
              <Route element={<MaintenanceGate area="profile" />}>
                <Route path="/profile" element={<ProfilePage />} />
              </Route>
              <Route element={<MaintenanceGate area="billing" />}>
                <Route path="/change-plan/invoice" element={<ChangePlanInvoicePage />} />
              </Route>
              <Route element={<MaintenanceGate area="reports" />}>
                <Route path="/report/:id" element={<ReportPage />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </div>
    </Router>
  );
};

export default App;
